import React, { useEffect, useRef, useState } from 'react';
import { CardDieCut } from '../routes/CardCustomizer/components/CardDieCut';
import { ENVELOPE_BACK, ENVELOPE_FRONT } from '@apps/card/constants/envelopeImages';

const PREVIEW_SCALE = 3; // As defined by Card Service
export const BLANK_ENVELOPE_FOREGROUND = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1566" height="1134" fill="red" />')}`;

type PreviewImages = {
  cardFront?: string | null | undefined;
  cardBack?: string | null | undefined;
  envelopFront?: string | null | undefined;
  envelopeBack?: string | null | undefined;
};

export async function loadImage(src: string | null | undefined, crossOriginAnonymous = false): Promise<HTMLImageElement | undefined> {
  if (!src) return undefined;
  return new Promise(resolve => {
    const image = new Image();
    if (crossOriginAnonymous) {
      // Otherwise when localhost: Failed to execute 'toBlob' on 'HTMLCanvasElement': Tainted canvases may not be exported.
      // However, Azure Blob URLs will break if 'anonymous'. You will get:
      //   Access to image at 'https://devprintjobs.blob.core.windows.net/...' from origin 'https://dev.withjoy.com' has been blocked by CORS policy:
      //   No 'Access-Control-Allow-Origin' header is present on the requested resource.
      image.crossOrigin = 'anonymous';
    }
    image.addEventListener('load', () => {
      resolve(image);
    });
    // A failed face is skipped like a missing one; without this the promise never settles.
    image.addEventListener('error', () => resolve(undefined));
    image.src = src;
  });
}

function createCanvas(width: number, height: number, draw: (ctx: CanvasRenderingContext2D) => void) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  draw(canvas.getContext('2d')!);
  return canvas;
}

type PageSize = Readonly<{ width: number; height: number }>;
type CornerRadii = [number, number, number, number];

/** A composited page plus the corner radii it was cut to, in page pixels. */
type PreviewPage = Readonly<{ canvas: HTMLCanvasElement; radii: CornerRadii }>;

/** Shared by the canvas clip and `cssDieCutRadius`, so a shadow can't drift from the cut. */
function dieCutRadii(diecut: CardDieCut, page: PageSize): CornerRadii {
  const radius = 54 * PREVIEW_SCALE;
  const diecuts: Record<CardDieCut, CornerRadii> = {
    rectangle: [0, 0, 0, 0],
    rounded: [radius, radius, radius, radius],
    arch: [page.width / 2, page.width / 2, 0, 0],
  };
  // `diecut` comes from draft JSON and product attributes, so an unknown string can reach here
  // past the type. Square corners are what `roundRect` already fell back to.
  return diecuts[diecut] ?? diecuts.rectangle;
}

/**
 * The cut as a CSS `border-radius`, so a box-shadow can follow it. Each radius is a fixed
 * fraction of the page, so percentages hold at any rendered size. The `h / v` form is
 * required — a single percentage goes elliptical on a page that isn't square.
 */
function cssDieCutRadius(radii: CornerRadii, page: PageSize) {
  const alongAxis = (extent: number) => radii.map(radius => `${((radius / extent) * 100).toFixed(4)}%`).join(' ');
  return `${alongAxis(page.width)} / ${alongAxis(page.height)}`;
}

async function createCardCanvas(imagePromise: Promise<HTMLImageElement | undefined>, diecut: CardDieCut): Promise<PreviewPage | undefined> {
  const bleed = 9 * PREVIEW_SCALE;
  const image = await imagePromise;
  if (!image) return undefined;

  const page = {
    width: image.width - bleed * 2,
    height: image.height - bleed * 2,
  };
  const radii = dieCutRadii(diecut, page);

  const canvas = createCanvas(page.width, page.height, ctx => {
    ctx.roundRect(0, 0, page.width, page.height, radii);
    ctx.clip();

    ctx.drawImage(image, -bleed, -bleed);
  });

  return { canvas, radii };
}

async function createEnvelopeCanvas(
  imagePromise: Promise<HTMLImageElement | undefined>,
  backgroundPromise: Promise<HTMLImageElement | undefined>,
): Promise<PreviewPage | undefined> {
  const image = await imagePromise;
  const background = await backgroundPromise;
  if (!image || !background) return undefined;

  const canvas = createCanvas(image.width, image.height, ctx => {
    ctx.drawImage(background, 0, 0, image.width, image.height);
    ctx.drawImage(image, 0, 0);
  });

  // Envelopes aren't die-cut — they keep square corners.
  return { canvas, radii: [0, 0, 0, 0] };
}

interface DraftPreviewProps {
  images: PreviewImages;
  diecut?: CardDieCut;
  /**
   * A CSS `box-shadow`, shaped to the die-cut and replacing the default drop-shadow. Pass this
   * rather than wrapping `DraftPreview` yourself: the cut is in the bitmap's alpha, not the
   * element's border box, so an outside box-shadow squares off rounded and arch corners.
   * Single-page previews only; a stacked composite keeps the default.
   */
  shadow?: string;
  /** Shown until the first bitmap is drawn, in place of the canvas's default 2:1 box. */
  placeholder?: React.ReactNode;
}

const DEFAULT_SHADOW = 'drop-shadow(0px 10px 10px rgba(0, 0, 0, 0.2))';

export const DraftPreview: React.FC<DraftPreviewProps> = ({ images, diecut = CardDieCut.rectangle, shadow, placeholder }) => {
  const { cardFront, cardBack, envelopFront, envelopeBack } = images;

  const ref = useRef<HTMLCanvasElement>(null);
  const [dieCutRadius, setDieCutRadius] = useState<string>();
  const [isDrawn, setIsDrawn] = useState(false);
  useEffect(() => {
    // A slower, earlier run must not paint over a newer one, or update state after unmount.
    let cancelled = false;
    void (async () => {
      const gap = 32 * PREVIEW_SCALE;
      const pages = (
        await Promise.all([
          createCardCanvas(loadImage(cardFront), diecut),
          createCardCanvas(loadImage(cardBack), diecut),
          // A stored order keeps `''` for a face that was never rendered (the schema's url fields are
          // non-null); `loadImage` treats that like a missing face.
          createEnvelopeCanvas(loadImage(envelopFront), loadImage(ENVELOPE_FRONT, true)),
          createEnvelopeCanvas(loadImage(envelopeBack), loadImage(ENVELOPE_BACK, true)),
        ])
      ).filter(page => page !== undefined) as PreviewPage[]; // VSCode infers PreviewPage[], but the joy-web unit tests don't for some reason.

      if (cancelled) return;
      // Nothing to draw (every face missing or failed): sizing an empty composite goes negative,
      // and the placeholder, if any, is the better stand-in than an empty canvas.
      if (pages.length === 0) return;

      const canvas = ref.current;
      if (!canvas) return; // Possibly umounted.

      const logicalWidth = pages.reduce((maxWidth, page) => Math.max(maxWidth, page.canvas.width), 0);
      const logicalHeight = pages.reduce((totalHeight, page) => totalHeight + page.canvas.height, 0) + (pages.length - 1) * gap;

      // Scale the canvas backing store by devicePixelRatio so the browser
      // doesn't upscale when CSS-sizing to the container. Without this, a 2×
      // retina display stretches ~918 backing px into ~1440 physical px,
      // producing visible softness on photo details like hair and bokeh.
      //
      // Area-safe cap: a 4-page composite (front + back + 2 envelopes) can
      // reach ~30M pixels at a flat 2× DPR, exceeding iOS Safari's ~16.7M
      // pixel limit on 4GB devices (blank canvas). We derive the max safe
      // DPR from the actual composite dimensions, floored at 1× so we never
      // regress below pre-PR behavior.
      const MAX_CANVAS_PIXELS = 16_000_000; // Conservative vs iOS Safari's 16,777,216 actual limit
      const deviceDpr = Math.min(window.devicePixelRatio || 1, 2);
      const areaSafeDpr = Math.sqrt(MAX_CANVAS_PIXELS / (logicalWidth * logicalHeight));
      const dpr = Math.max(1, Math.min(deviceDpr, areaSafeDpr));

      canvas.width = logicalWidth * dpr;
      canvas.height = logicalHeight * dpr;
      const ctx = canvas.getContext('2d');
      if (!ctx) return; // Canvas too large for device — iOS Safari returns null.
      ctx.imageSmoothingQuality = 'high';
      ctx.scale(dpr, dpr);

      let y = 0;
      for (const { canvas: page } of pages) {
        const x = (logicalWidth - page.width) / 2;
        ctx.drawImage(page, x, y, page.width, page.height);
        y += page.height + gap;
      }

      // Only a single-page canvas has one corner shape a `shadow` can follow.
      const [onlyPage] = pages;
      const followable = shadow !== undefined && pages.length === 1 && onlyPage;
      setDieCutRadius(followable ? cssDieCutRadius(onlyPage.radii, onlyPage.canvas) : undefined);
      setIsDrawn(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [cardBack, cardFront, diecut, envelopFront, envelopeBack, shadow]);

  // No page dimensions until the first image resolves, so hold the default until there are.
  const shadowFollowsDieCut = shadow !== undefined && dieCutRadius !== undefined;

  return (
    <>
      {!isDrawn && placeholder}
      <canvas
        ref={ref}
        // `display: block` takes the canvas out of the line box. As an inline
        // element it sits on the text baseline, adding ~4px of descender space
        // below it — which offsets the artwork inside centered containers and
        // detaches the box-shadow from the card edge.
        //
        // The shadow goes on the canvas itself, never a wrapper: swapping the element
        // structure once `dieCutRadius` resolves would remount the canvas, orphaning the
        // bitmap the effect already drew. The radius matches the alpha edge, so it clips nothing.
        style={{
          display: isDrawn || !placeholder ? 'block' : 'none',
          width: '100%',
          borderRadius: dieCutRadius,
          boxShadow: shadowFollowsDieCut ? shadow : undefined,
          filter: shadowFollowsDieCut ? undefined : DEFAULT_SHADOW,
        }}
      />
    </>
  );
};
